import {
  getArenaReservableSeatGroups,
  getArenaSendMethodSourceCode,
  getArenaReservationFailureReason,
} from './arenaSeatSelectionUtils'
import type {
  ArenaReservationFailureReason,
  ArenaReserveResult,
  ArenaSelectedSeat,
  ArenaSendMethod,
} from './arenaSeatSelectionUtils'

interface CreateArenaTrolleyTokenParams {
  apiAuthToken: string
  apiBase: string
  perfId: string
  selectedSeats: Array<ArenaSelectedSeat>
  sendMethod: ArenaSendMethod
}

interface ValidateArenaTrolleyReservationParams {
  apiAuthToken: string
  apiBase: string
  sendMethod: ArenaSendMethod
  trolleyToken: string
}

type ArenaTrolleyResponse = {
  results?: {
    trolley_token?: string
  }
  trolley_token?: string
}

export interface ArenaTrolleyReservationValidationResult {
  failureReason?: ArenaReservationFailureReason
  isValid: boolean
}

function getArenaTrolleyResponseToken(response: ArenaTrolleyResponse) {
  return response.trolley_token ?? response.results?.trolley_token
}

function buildArenaUrl(apiBase: string, path: string) {
  return `${apiBase.replace(/\/$/, '')}/${path}`
}

function buildTrolleyUrl(apiBase: string, params: URLSearchParams) {
  return `${buildArenaUrl(apiBase, 'trolley.v1')}?${params.toString()}`
}

function getArenaTrolleyLogContext(group: ReturnType<typeof getArenaReservableSeatGroups>[number], perfId: string) {
  return {
    noOfSeats: group.seats.length,
    perfId,
    priceBandCode: group.priceBandCode,
    seatIds: group.seats.map(seat => seat.seatId),
    ticketTypeCode: group.ticketTypeCode,
  }
}

export async function createArenaTrolleyToken(params: CreateArenaTrolleyTokenParams) {
  const { apiAuthToken, apiBase, perfId, selectedSeats, sendMethod } = params
  const seatGroups = getArenaReservableSeatGroups(selectedSeats)
  const sourceCode = getArenaSendMethodSourceCode(sendMethod)
  let trolleyToken: string | undefined

  for (const group of seatGroups) {
    const searchParams = new URLSearchParams({
      [`${sourceCode}_send_code`]: sendMethod.send_code,
      no_of_seats: group.seats.length.toString(),
      perf_id: perfId,
      price_band_code: group.priceBandCode,
      ticket_type_code: group.ticketTypeCode,
    })

    if (trolleyToken) searchParams.set('trolley_token', trolleyToken)

    group.seats.forEach((seat, index) => {
      searchParams.set(`disc${index}`, group.discountCode)
      searchParams.set(`seat${index}`, seat.seatId)
    })

    const response = await fetch(buildTrolleyUrl(apiBase, searchParams), {
      headers: {
        Authorization: `Bearer ${apiAuthToken}`,
      },
    })

    if (!response.ok) {
      console.warn('[Arena] trolley token request failed', {
        ...getArenaTrolleyLogContext(group, perfId),
        status: response.status,
      })
      throw new Error('Arena trolley token request failed')
    }

    trolleyToken = getArenaTrolleyResponseToken(await response.json())
    if (!trolleyToken) {
      console.warn('[Arena] trolley token response did not include a token', getArenaTrolleyLogContext(group, perfId))
      throw new Error('Arena trolley token response did not include a token')
    }
  }

  return trolleyToken
}

async function postArenaForm<TResponse>(params: { apiAuthToken: string, apiBase: string, body: URLSearchParams, path: string }) {
  const response = await fetch(buildArenaUrl(params.apiBase, params.path), {
    body: params.body.toString(),
    headers: {
      Authorization: `Bearer ${params.apiAuthToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    method: 'POST',
  })

  if (!response.ok) {
    throw new Error(`Arena ${params.path} request failed`)
  }

  const responseText = await response.text()
  return (responseText ? JSON.parse(responseText) : undefined) as TResponse | undefined
}

function hasReservedTrolley(response: ArenaReserveResult) {
  return !!response.trolley_contents?.transaction_uuid &&
    !response.input_contained_unavailable_order &&
    !response.unreserved_orders?.length
}

export async function validateArenaTrolleyReservation(params: ValidateArenaTrolleyReservationParams): Promise<ArenaTrolleyReservationValidationResult> {
  const sourceCode = getArenaSendMethodSourceCode(params.sendMethod)
  const reserveBody = new URLSearchParams({
    [`${sourceCode}_send_code`]: params.sendMethod.send_code,
    trolley_token: params.trolleyToken,
  })
  const reserveResponse = await postArenaForm<ArenaReserveResult>({
    apiAuthToken: params.apiAuthToken,
    apiBase: params.apiBase,
    body: reserveBody,
    path: 'reserve.v1',
  })

  if (!reserveResponse) {
    return { isValid: false, failureReason: 'reserve-failed' }
  }

  const transactionUuid = reserveResponse.trolley_contents?.transaction_uuid
  if (transactionUuid) {
    const releaseBody = new URLSearchParams({ transaction_uuid: transactionUuid })
    try {
      await postArenaForm<void>({
        apiAuthToken: params.apiAuthToken,
        apiBase: params.apiBase,
        body: releaseBody,
        path: 'release.v1',
      })
    } catch {
      // Release is best-effort cleanup; the transaction will auto-expire.
    }
  }

  const isValid = hasReservedTrolley(reserveResponse)
  return {
    isValid,
    failureReason: isValid ? undefined : getArenaReservationFailureReason(reserveResponse),
  }
}
